import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Button, ExpandableSection, Spinner, Box, Icon } from '@amzn/awsui-components-core';
import {
  InlineComment,
  UserCommentState,
  ContentType,
} from '@amzn/awscat-pattern-library-service-model';

import constants from '../../common/constants';
import '../../resources/styles/scss/components/inline-comment/inline-comment-list.scss';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import {
  categorizeInlineCommentsBySection,
  categorizeCommentsByThread,
  truncateTextData,
  TECH_WRITER_VIEWER_LINK,
  getFinalizingInlineComments,
} from '../utils/InlineCommentUtils';
import InlineCommentThread from './InlineCommentThread';
import InlineCommentReply from './InlineCommentReply';
import InlineCommentCtx, { InlineCommentCtxData } from './InlineCommentCtx';
import {
  ContentViewerContextData,
  withContentViewerContext,
} from '../workflow-viewer/ContentViewerContext';

interface Props {
  setShowAllComment: (value: boolean) => void;
  contentId: string;
  workflowId: number;
  contentViewerContext: ContentViewerContextData;
}

const mplServiceClient: IMplServiceClient = MplServiceClientFactory.create();

const InlineCommentList = (props: Props) => {
  const [inlineComments, setInlineComments] = useState<InlineComment[]>([]);
  const [isFetchingComments, setIsFetchingComments] = useState<boolean>(false);

  const { contentId, workflowId } = props;

  useEffect(() => {
    const { inlineComments: updatedInlineComments } = props?.contentViewerContext;
    setInlineComments(updatedInlineComments ?? []);
  }, [props?.contentViewerContext]);

  const handleCloseComments = () => {
    const { setShowAllComment } = props;
    props.contentViewerContext?.enableCommentMode?.(false);
    setShowAllComment(false);
  };

  const fetchInlineComments = useCallback(async () => {
    setIsFetchingComments(true);
    const response = await mplServiceClient.listInlineComments(
      contentId,
      props?.contentViewerContext?.workflowId ?? props.workflowId,
    );
    if (window.location.pathname.includes(TECH_WRITER_VIEWER_LINK)) {
      setInlineComments(getFinalizingInlineComments(response.inlineComments));
    } else {
      setInlineComments(response.inlineComments);
    }
    setIsFetchingComments(false);
  }, [contentId, props?.contentViewerContext?.workflowId, props.workflowId]);

  useEffect(() => {
    fetchInlineComments();
  }, [fetchInlineComments]);

  const handleRefreshCommentList = async () => {
    await fetchInlineComments();
  };

  const renderToggleAndRefresh = () => {
    return (
      <Box padding={{ left: 's' }} float="right">
        <div className="inline-comment-toggle-container">
          <Button
            iconName="refresh"
            variant="icon"
            ariaLabel="Refresh comments"
            onClick={handleRefreshCommentList}
          />
        </div>
      </Box>
    );
  };

  const renderComments = (commentsToRender: InlineComment[]) => {
    // @ts-expect-error TS18048 - strict migration
    const contentType = props.contentViewerContext.content.contentMetadata
      .contentType as ContentType;
    const categorizedComments = categorizeInlineCommentsBySection(commentsToRender, contentType);
    const sectionType = contentType === ContentType.Pattern ? 'section' : 'page';

    return (
      <>
        {renderToggleAndRefresh()}
        {categorizedComments.map((sectionComments) => {
          const { section, comments } = sectionComments;
          return (
            <div key={section}>
              <ExpandableSection
                headerText={`${section} ${sectionType} (${comments.length})`}
                defaultExpanded
              >
                {renderSectionComments(comments)}
              </ExpandableSection>
            </div>
          );
        })}
      </>
    );
  };

  const renderOpenComments = (comments: InlineComment[]) => {
    if (comments.length === 0) {
      return (
        <>
          {renderToggleAndRefresh()}
          <Box padding={{ left: 's' }}>No comment.</Box>
        </>
      );
    }
    return renderComments(comments);
  };

  const renderResolvedComments = (comments: InlineComment[]) => {
    if (comments.length === 0) {
      return (
        <>
          {renderToggleAndRefresh()}
          <Box padding={{ left: 's' }}>No resolved comment.</Box>
        </>
      );
    }
    return renderComments(comments);
  };

  const renderSectionComments = (sectionComments: InlineComment[]) => {
    const categorizedComments = categorizeCommentsByThread(sectionComments);
    return (
      <div>
        {categorizedComments.map((commentThread) => {
          const { textData, comments } = commentThread;
          return (
            // @ts-expect-error TS2532 - strict migration
            <div key={`${textData}${comments[0].commentId}`}>
              <div className="inline-comment-list-text-data-container">
                <span className="inline-comment-list-text-data">{truncateTextData(textData)}</span>
              </div>
              {comments.map((comment) => {
                return (
                  <div key={comment.commentId}>
                    <InlineCommentThread
                      comment={comment}
                      contentId={contentId}
                      workflowId={workflowId}
                      fetchInlineComments={fetchInlineComments}
                    />
                  </div>
                );
              })}
              <InlineCommentReply
                contentId={contentId}
                workflowId={workflowId}
                // @ts-expect-error TS2322 - strict migration
                threadComment={comments[0]}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const inlineCommentCtxData: InlineCommentCtxData = {
    allInlineComments: inlineComments,
    setAllInlineComments: setInlineComments,
    fetchInlineComments,
  };
  const openComments = inlineComments.filter(
    (comment) => comment.state === UserCommentState.PENDING,
  );
  const resolvedComments = inlineComments.filter(
    (comment) => comment.state === UserCommentState.RESOLVED,
  );

  const openCommentsToTender = isFetchingComments ? <Spinner /> : renderOpenComments(openComments);
  const resolvedCommentsToRender = isFetchingComments ? (
    <Spinner />
  ) : (
    renderResolvedComments(resolvedComments)
  );

  const isCommentingModeEnabled = props?.contentViewerContext?.isCommentingModeEnabled;
  const headerClassName = `inline-comment-list-header${isCommentingModeEnabled ? ' inline-comment-header-min-width' : ''}`;
  return (
    <InlineCommentCtx.Provider value={inlineCommentCtxData}>
      <div className={headerClassName}>
        <div className="inline-comment-list-header-tab">
          <Tabs
            ariaLabel="Inline comments navigation"
            tabs={[
              {
                label: `${constants.INLINE_COMMENT_OPEN_COMMENTS} (${openComments.length})`,
                id: constants.INLINE_COMMENT_OPEN_COMMENTS,
                content: openCommentsToTender,
              },
              {
                label: `${constants.INLINE_COMMENT_RESOLVED_COMMENTS} (${resolvedComments.length})`,
                id: constants.INLINE_COMMENT_RESOLVED_COMMENTS,
                content: resolvedCommentsToRender,
              },
            ]}
          />
        </div>
        <div className="inline-comment-list-hide-comments">
          <Button
            iconName="close"
            variant="icon"
            ariaLabel="Close comments"
            onClick={handleCloseComments}
          />
        </div>
      </div>
    </InlineCommentCtx.Provider>
  );
};

export default withContentViewerContext(InlineCommentList);
